import { inject, Injectable } from '@angular/core'
import { Observable, Subject, finalize } from 'rxjs'
import {
  InterventionsService,
  BatchInterventionResult,
  BatchSlotResult,
} from '../../interventions/services/interventions.service'

/**
 * Slot indicator summary for an intervention
 */
export interface SlotIndicator {
  feasibleCount: number
  adjustableCount: number
  shiftCount: number
  totalSlots: number
  calculated: boolean
}

/**
 * Constrained slot counts after applying time constraints
 */
export interface ConstrainedSlotCounts {
  feasibleCount: number
  adjustableCount: number
  shiftCount: number
  totalSlots: number
}

/**
 * Slot travel info for available slots
 */
export interface SlotTravelInfo {
  travelFromPrevious: number | null
  travelToNext: number | null
  travelFromHome: number | null
  travelToHome: number | null
  homeDepartureTime: Date | null
  homeReturnTime: Date | null
  isFirstSlotOfDay: boolean
  isLastSlotOfDay: boolean
  homeDepartureIsTight: boolean
  homeReturnIsLate: boolean
  totalTravelTime: number
  availableTime: number
  effectiveAvailableTime: number
  requiredTime: number
  isFeasible: boolean
  needsAdjustment: boolean
  previousEvent: any | null
  nextEvent: any | null
  arrivalDelay: number
  previousEventEnd: Date | null
  estimatedArrival: Date | null
  nextEventStart: Date | null
  latestDeparture: Date | null
  // do_not_shift flags for adjacent events - if true, event cannot be shifted
  previousEventDoNotShift: boolean | null
  nextEventDoNotShift: boolean | null
  // Scenario insertion cost (Valhalla N×N route evaluation)
  scenarioAddedKm: number | null
  scenarioTotalKm: number | null
  scenarioAddedMin: number | null
  // Shift-slot: requires shifting an existing (unlocked) intervention
  requiresShift: boolean
  shiftedEventId: number | null
  shiftedEventTitle: string | null
}

/**
 * Affair data required for indicator calculations
 */
export interface AffairIndicatorData {
  workSiteLatitude: number
  workSiteLongitude: number
}

/**
 * Service responsible for calculating and caching intervention slot indicators
 */
@Injectable({
  providedIn: 'root',
})
export class InterventionIndicatorService {
  private interventionsService = inject(InterventionsService)

  // Cache for batch indicators by intervention ID
  public batchIndicatorsCache: Map<number, BatchInterventionResult> = new Map()

  // Cache for slot indicators by intervention ID
  public interventionSlotIndicators: Map<number, SlotIndicator> = new Map()

  // Slot travel info by slot index
  public slotTravelInfo: Map<number, SlotTravelInfo> = new Map()

  // Loading states
  public loadingBatchIndicators = false
  public loadingSlotTravelInfo = false

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.batchIndicatorsCache.clear()
    this.interventionSlotIndicators.clear()
    this.slotTravelInfo.clear()
  }

  /**
   * Clear slot travel info
   */
  clearSlotTravelInfo(): void {
    this.slotTravelInfo.clear()
  }

  /**
   * Get slot indicator for an intervention
   */
  getInterventionSlotIndicator(interventionId: number): SlotIndicator | null {
    return this.interventionSlotIndicators.get(interventionId) || null
  }

  /**
   * Get batch indicator result for an intervention
   */
  getBatchIndicator(interventionId: number): BatchInterventionResult | null {
    return this.batchIndicatorsCache.get(interventionId) || null
  }

  /**
   * Get slot travel info by index
   */
  getSlotTravelInfo(slotIndex: number): SlotTravelInfo | null {
    return this.slotTravelInfo.get(slotIndex) || null
  }

  /**
   * Get slot travel info by original index
   */
  getSlotTravelInfoByOriginalIndex(
    originalIndex: number
  ): SlotTravelInfo | null {
    return this.slotTravelInfo.get(originalIndex) || null
  }

  /**
   * Fetch batch slot indicators for all interventions
   * @param affair The affair to schedule
   * @param dateFrom Start date
   * @param dateTo End date
   * @param duration Intervention duration in minutes
   * @param productsNames Product names to filter
   * @param closestInterventions Pre-fetched closest interventions
   * @param limit Maximum number of interventions
   */
  fetchBatchSlotIndicators(
    affair: AffairIndicatorData,
    dateFrom: string,
    dateTo: string,
    duration: number,
    productsNames: string[],
    closestInterventions: any[],
    limit: number
  ): Observable<{ success: boolean; hasInterventions: boolean }> {
    const result$ = new Subject<{
      success: boolean
      hasInterventions: boolean
    }>()

    if (!closestInterventions || closestInterventions.length === 0) {
      result$.next({ success: true, hasInterventions: false })
      result$.complete()
      return result$.asObservable()
    }

    // Map interventions for batch request
    const closestInterventionsData = closestInterventions.map(
      (intervention: any) => ({
        id: intervention.id,
        erpSource: intervention.erpSource,
        technician_email: intervention.usersTechnicians?.[0]?.email || null,
        distance_km: intervention.distance,
        duration_min: intervention.duration_min,
        start_date: intervention.startDate,
        workSiteLatitude: intervention.affair?.workSiteLatitude,
        workSiteLongitude: intervention.affair?.workSiteLongitude,
      })
    )

    this.loadingBatchIndicators = true

    this.interventionsService
      .batchSlotIndicators({
        affair: {
          workSiteLatitude: affair.workSiteLatitude,
          workSiteLongitude: affair.workSiteLongitude,
        },
        date_from: dateFrom,
        date_to: dateTo,
        duration,
        products_names: productsNames,
        limit,
        closest_interventions: closestInterventionsData,
      })
      .pipe(finalize(() => (this.loadingBatchIndicators = false)))
      .subscribe({
        next: (response) => {
          if (response.status === 'success' && response.interventions) {
            this.batchIndicatorsCache.clear()
            this.interventionSlotIndicators.clear()

            response.interventions.forEach(
              (batchResult: BatchInterventionResult) => {
                if (batchResult.intervention_id) {
                  this.batchIndicatorsCache.set(
                    batchResult.intervention_id,
                    batchResult
                  )

                  // Calculate constrained counts
                  const constrainedCounts =
                    this.calculateConstrainedSlotCounts(batchResult)
                  this.interventionSlotIndicators.set(
                    batchResult.intervention_id,
                    {
                      feasibleCount: constrainedCounts.feasibleCount,
                      adjustableCount: constrainedCounts.adjustableCount,
                      shiftCount: constrainedCounts.shiftCount,
                      totalSlots: constrainedCounts.totalSlots,
                      calculated: true,
                    }
                  )
                }
              }
            )

            result$.next({ success: true, hasInterventions: true })
            result$.complete()
          } else {
            result$.next({ success: false, hasInterventions: false })
            result$.complete()
          }
        },
        error: (error) => {
          console.error('Error fetching batch slot indicators:', error)
          result$.error(error)
        },
      })

    return result$.asObservable()
  }

  /**
   * Restore batch indicators from persisted data
   */
  restoreBatchIndicatorsFromData(data: { [key: number]: any }): void {
    this.batchIndicatorsCache.clear()
    this.interventionSlotIndicators.clear()

    for (const [idStr, result] of Object.entries(data)) {
      const interventionId = parseInt(idStr, 10)
      if (result && interventionId) {
        this.batchIndicatorsCache.set(interventionId, result)

        const constrainedCounts = this.calculateConstrainedSlotCounts(result)
        this.interventionSlotIndicators.set(interventionId, {
          feasibleCount: constrainedCounts.feasibleCount,
          adjustableCount: constrainedCounts.adjustableCount,
          shiftCount: constrainedCounts.shiftCount,
          totalSlots: constrainedCounts.totalSlots,
          calculated: true,
        })
      }
    }
  }

  /**
   * Export batch indicators for persistence
   */
  exportBatchIndicators(): { [key: number]: BatchInterventionResult } {
    const data: { [key: number]: BatchInterventionResult } = {}
    this.batchIndicatorsCache.forEach((value, key) => {
      data[key] = value
    })
    return data
  }

  /**
   * Calculate constrained slot counts after applying time constraints
   */
  calculateConstrainedSlotCounts(
    result: BatchInterventionResult
  ): ConstrainedSlotCounts {
    let feasibleCount = 0
    let adjustableCount = 0
    let shiftCount = 0
    let blockedCount = 0

    if (!result.slots || result.slots.length === 0) {
      return {
        feasibleCount: result.feasible_slots || 0,
        adjustableCount: result.adjustable_slots || 0,
        shiftCount: 0,
        totalSlots: result.total_slots || 0,
      }
    }

    // Apply time constraints to each slot
    for (const slot of result.slots) {
      // Check constraints
      if (!this.batchSlotMeetsTimeConstraints(slot)) {
        continue
      }

      // Les shift-slots ont leur propre catégorie
      if (slot.requires_shift) {
        shiftCount++
        continue
      }

      if (slot.is_feasible) {
        feasibleCount++
      } else if (slot.needs_adjustment) {
        // Check if the slot can actually be adjusted (not blocked by do_not_shift)
        if (this.batchSlotCanBeAdjusted(slot)) {
          adjustableCount++
        } else {
          blockedCount++
        }
      }
    }

    return {
      feasibleCount,
      adjustableCount,
      shiftCount,
      totalSlots: result.slots.length,
    }
  }

  /**
   * Check if a batch slot that needs adjustment can actually be adjusted
   * A slot cannot be adjusted if adjacent events have do_not_shift = true
   */
  private batchSlotCanBeAdjusted(slot: BatchSlotResult): boolean {
    // If either adjacent event has do_not_shift = true, the slot cannot be adjusted
    if (slot.previous_event_do_not_shift === true) return false
    if (slot.next_event_do_not_shift === true) return false
    return true
  }

  /**
   * Check if a batch slot meets time constraints
   */
  private batchSlotMeetsTimeConstraints(slot: BatchSlotResult): boolean {
    // Check home departure time constraint (not before 7:30)
    if (slot.is_first_slot_of_day && slot.home_departure_time) {
      const departureDate = new Date(slot.home_departure_time)
      const departureHour = departureDate.getHours()
      const departureMinute = departureDate.getMinutes()
      if (departureHour < 7 || (departureHour === 7 && departureMinute < 0)) {
        return false
      }
    }

    // Check home return time constraint (not after 20:00)
    if (slot.is_last_slot_of_day && slot.home_return_time) {
      const returnDate = new Date(slot.home_return_time)
      const returnHour = returnDate.getHours()
      if (returnHour >= 20) {
        return false
      }
    }

    return true
  }

  /**
   * Populate slot travel info from batch result slots
   */
  populateSlotTravelInfoFromBatch(
    slots: BatchSlotResult[],
    interventionDuration: number
  ): void {
    this.slotTravelInfo.clear()

    slots.forEach((slot, index) => {
      this.slotTravelInfo.set(index, {
        travelFromPrevious: slot.travel_from_previous_min,
        travelToNext: slot.travel_to_next_min,
        travelFromHome: slot.travel_from_home_min,
        travelToHome: slot.travel_to_home_min,
        homeDepartureTime: slot.home_departure_time
          ? new Date(slot.home_departure_time)
          : null,
        homeReturnTime: slot.home_return_time
          ? new Date(slot.home_return_time)
          : null,
        isFirstSlotOfDay: slot.is_first_slot_of_day,
        isLastSlotOfDay: slot.is_last_slot_of_day,
        homeDepartureIsTight: slot.home_departure_is_tight,
        homeReturnIsLate: slot.home_return_is_late,
        totalTravelTime:
          (slot.travel_from_previous_min || slot.travel_from_home_min || 0) +
          (slot.travel_to_next_min || slot.travel_to_home_min || 0),
        availableTime: slot.available_minutes,
        effectiveAvailableTime: slot.effective_available_minutes,
        requiredTime: interventionDuration,
        isFeasible: slot.is_feasible,
        needsAdjustment: slot.needs_adjustment,
        previousEvent: null,
        nextEvent: null,
        arrivalDelay: 0,
        previousEventEnd: null,
        nextEventStart: null,
        estimatedArrival: slot.estimated_arrival
          ? new Date(slot.estimated_arrival)
          : new Date(slot.start),
        latestDeparture: slot.latest_departure
          ? new Date(slot.latest_departure)
          : new Date(slot.end),
        previousEventDoNotShift: slot.previous_event_do_not_shift ?? null,
        nextEventDoNotShift: slot.next_event_do_not_shift ?? null,
        scenarioAddedKm: slot.scenario_added_km ?? null,
        scenarioTotalKm: slot.scenario_total_km ?? null,
        scenarioAddedMin: slot.scenario_added_min ?? null,
        requiresShift: slot.requires_shift ?? false,
        shiftedEventId: slot.shifted_event_id ?? null,
        shiftedEventTitle: slot.shifted_event_title ?? null,
      })
    })

    this.loadingSlotTravelInfo = false
  }

  /**
   * Get count of feasible slots
   * Excludes shift-slots which have their own category
   */
  getFeasibleSlotsCount(): number {
    let count = 0
    const totalSlots = this.slotTravelInfo.size

    for (let i = 0; i < totalSlots; i++) {
      const info = this.slotTravelInfo.get(i)
      // Exclure les shift-slots - ils ont leur propre catégorie "décaler"
      if (info?.requiresShift) continue
      if (this.slotMeetsAllConstraints(i)) {
        count++
      }
    }
    return count
  }

  /**
   * Get count of adjustable slots
   */
  getAdjustableSlotsCount(): number {
    let count = 0
    const totalSlots = this.slotTravelInfo.size

    for (let i = 0; i < totalSlots; i++) {
      const info = this.slotTravelInfo.get(i)
      if (info?.needsAdjustment) count++
    }
    return count
  }

  /**
   * Check if slot meets all constraints
   */
  private slotMeetsAllConstraints(slotIndex: number): boolean {
    const info = this.slotTravelInfo.get(slotIndex)
    if (!info) return false

    // For slots that need adjustment (SERRÉ), they are still valid
    // because if we shift adjacent events, we can use the full slot
    if (info.needsAdjustment) {
      // Check time constraints
      return this.slotMeetsTimeConstraints(slotIndex, info)
    }

    // Must be feasible for OK slots
    if (!info.isFeasible) return false

    // Check time constraints
    return this.slotMeetsTimeConstraints(slotIndex, info)
  }

  /**
   * Check if slot meets time constraints
   */
  private slotMeetsTimeConstraints(
    slotIndex: number,
    info: SlotTravelInfo
  ): boolean {
    // Check home departure constraint
    if (info.isFirstSlotOfDay && info.homeDepartureTime) {
      const departureHour = info.homeDepartureTime.getHours()
      if (departureHour < 7) {
        return false
      }
    }

    // Check home return constraint
    if (info.isLastSlotOfDay && info.homeReturnTime) {
      const returnHour = info.homeReturnTime.getHours()
      if (returnHour >= 20) {
        return false
      }
    }

    return true
  }

  /**
   * Get sorted available slots with their original indices
   */
  getSortedAvailableSlots(
    availabilities: any
  ): { slot: any; originalIndex: number }[] {
    if (!availabilities?.availabilities?.available_slots) {
      return []
    }

    const slots = availabilities.availabilities.available_slots
    const indexedSlots = slots.map((slot: any, index: number) => ({
      slot,
      originalIndex: index,
    }))

    // Sort by date then by start time
    return indexedSlots.sort((a: any, b: any) => {
      const dateA = new Date(a.slot.start).toDateString()
      const dateB = new Date(b.slot.start).toDateString()

      if (dateA !== dateB) {
        return (
          new Date(a.slot.start).getTime() - new Date(b.slot.start).getTime()
        )
      }

      return new Date(a.slot.start).getTime() - new Date(b.slot.start).getTime()
    })
  }
}
